<template>
  <div class="analysis-result-section">
    <div class="result-header">
      <h3>分析结果</h3>
      <div class="result-info">
        <span>已加入库 ({{ addedCount }}/{{ totalCount }})</span>
        <el-button type="primary" size="small" class="view-library-btn" @click="openSolutionDialog">查看解决方案</el-button>
      </div>
    </div>
    
    <!-- 解决方案库弹窗 -->
  <SolutionLibraryDialog 
    :visible="solutionDialogVisible"
    :tech-paths="selectedTechPaths"
    :patents="similarPatentsData"
    :triplet="tripletData"
    :project-id="projectId"
    @update:visible="solutionDialogVisible = $event"
    @generate-solution="(solutionData) => $emit('generate-solution', solutionData)"
    @triplet-updated="(updatedTriplet) => $emit('triplet-updated', updatedTriplet)"
  />

    <!-- 加载状态 -->
    <div class="loading-container" v-if="isLoading">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <p class="loading-hint">正在分析技术路径，大约需要几分钟时间，请稍候…</p>
    </div>

    <!-- 技术路径列表 -->
    <div class="tech-path-list" v-else>
      <!-- 没有数据时的提示 -->
      <div class="no-data" v-if="localTechPaths.length === 0">
        <el-empty description="暂无技术路径数据" />
      </div>
      
      <!-- 技术路径列表 -->
      <div 
        v-for="(path, index) in localTechPaths" 
        :key="path._uid || path.id || index"
        class="tech-path-item"
        :class="{ 'recommended': path.isRecommended }"
      >
        <div class="path-header">
          <div class="solution-header" v-if="path.isRecommended">
            <el-tag type="info">推荐方案</el-tag>
          </div>
          <h4>{{ path.title }}</h4>
        </div>
        <div class="path-description">
          <p>{{ path.description }}</p>
        </div>
        
        <!-- 推荐方案的解决方案描述和操作按钮 -->
        <div class="solution-description" v-if="path.isRecommended"> 
          <p>{{ path.solutionDescription }}</p>
        </div>
        
        <div class="path-tags">
          <el-tag 
            v-for="(tag, tagIndex) in path.tags" 
            :key="tagIndex"
            size="small"
          >{{ tag }}</el-tag>
        </div>
        <div class="path-pros">
          <div class="pros-header" @click="togglePros(index)">
            <el-icon>
              <ArrowUp v-if="prosExpanded[index]" />
              <ArrowDown v-else />
            </el-icon>
            <h5>显示优缺点分析</h5>
          </div>
          <div class="pros-content" v-if="prosExpanded[index]">
            <div class="pros-section" v-if="path.pros && path.pros.length > 0">
              <h6>优点：</h6>
              <ul>
                <li v-for="(item, itemIndex) in path.pros" :key="itemIndex">{{ item }}</li>
              </ul>
            </div>
            <div class="cons-section" v-if="path.cons && path.cons.length > 0">
              <h6>缺点：</h6>
              <ul>
                <li v-for="(item, itemIndex) in path.cons" :key="itemIndex">{{ item }}</li>
              </ul>
            </div>
            <div v-if="(!path.pros || path.pros.length === 0) && (!path.cons || path.cons.length === 0)">
              <p>暂无优缺点分析</p>
            </div>
          </div>
        </div>
        <div class="path-actions">
          <div class="path-buttons">
            <el-button style="background-color: #fff;border-color: #333;border-radius: 14px;font-size: 12px;padding: 5px 10px;" type="text" size="small" @click="viewDetail(path)">
              查看详情
            </el-button>
            <el-button style="background-color: #fff;border-color: #333;border-radius: 14px;font-size: 12px;padding: 5px 10px;" type="text" size="small" @click="viewSimilarPatents(path)">
              相关专利
            </el-button>
            <template v-if="!path.isAddedToLibrary">
              <el-button 
                type="primary" 
                size="small" 
                @click="addToLibrary(path, index)" style="background-color: #333;border-color: #333;border-radius: 14px;font-size: 12px;"
              >
                <el-icon><Plus /></el-icon>
                加入库
              </el-button>
            </template>
            <template v-else>
              <el-button 
                type="danger" 
                text 
                size="small" 
                @click="removeFromLibrary(index)"
                class="remove-library-btn"
              >
                <el-icon><Delete /></el-icon>
                从库中删除
              </el-button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, watch, computed } from 'vue'
import {
  InfoFilled,
  Plus,
  View,
  Document,
  ArrowUp,
  ArrowDown,
  Check,
  Delete,
  Loading
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import SolutionLibraryDialog from './SolutionLibraryDialog.vue'

const props = defineProps({
  // 新增：接收要分析的内容，用于调用API
  analysisContent: {
    type: String,
    default: ''
  },
  // 新增：接收外部传递的技术路径数据
  techPaths: {
    type: Array,
    default: () => []
  },
  // 新增：接收外部传递的专利数据
  patents: {
    type: Array,
    default: () => []
  },
  // 新增：接收外部传递的加载状态
  isLoading: {
    type: Boolean,
    default: false
  },
  // 新增：接收外部传递的最终问题三元组
  triplet: {
    type: Object,
    default: null
  },
  // 项目ID：弹窗从数据库读已入库专利用
  projectId: {
    type: [String, Number],
    default: null
  }
})
const tripletData = ref([])

watch(() => props.triplet, (newTriplet) => {
  console.log('newTriplet', newTriplet)
  if (newTriplet) {
    tripletData.value = newTriplet
  } else {
    tripletData.value = []
  }
})

// 排序函数：确保推荐方案排在第一位
const sortTechPaths = (techPaths) => {
  // 深拷贝数组，避免修改原数据
  const sortedPaths = [...techPaths]
  // 排序：推荐方案（isRecommended为true）排在前面，其他按原顺序
  return sortedPaths.sort((a, b) => {
    if (a.isRecommended && !b.isRecommended) return -1
    if (!a.isRecommended && b.isRecommended) return 1
    return 0
  })
}

// 本地技术路径数据，优先使用props传入的数据
const localTechPaths = ref(sortTechPaths(props.techPaths.map(path => ({
  ...path,
  isAddedToLibrary: false
}))))

// 监听props.techPaths变化，更新本地数据，保留已有的isAddedToLibrary状态
watch(
  () => props.techPaths,
  (newTechPaths) => {
    if (newTechPaths && newTechPaths.length > 0) {
      // 创建一个映射，保存现有技术路径的isAddedToLibrary状态
      const existingStatusMap = new Map()
      localTechPaths.value.forEach(path => {
        existingStatusMap.set(path.title, path.isAddedToLibrary)
      })
      
      // 更新本地数据，保留已有的isAddedToLibrary状态
      const updatedPaths = newTechPaths.map(path => ({
        ...path,
        // 如果是现有路径，保留原有状态；否则设为false
        isAddedToLibrary: existingStatusMap.has(path.title) ? existingStatusMap.get(path.title) : false
      }))
      
      // 排序，确保推荐方案排在第一位
      localTechPaths.value = sortTechPaths(updatedPaths)
    }
  },
  { deep: true }
)

// 监听props.isLoading变化
watch(
  () => props.isLoading,
  (newIsLoading) => {
    // 外部加载状态变化时的处理
    console.log('外部加载状态变化:', newIsLoading)
  }
)

// 监听props.patents变化，更新相似专利数据
watch(
  () => props.patents,
  (newPatents) => {
    similarPatentsData.value = newPatents || []
  },
  { deep: true }
)

// 计算已加入库的数量
const addedCount = computed(() => {
  return localTechPaths.value.filter(path => path.isAddedToLibrary).length
})

// 计算技术路径总数
const totalCount = computed(() => {
  return localTechPaths.value.length
})

// 计算被选中加入库的技术路径
const selectedTechPaths = computed(() => {
  return localTechPaths.value.filter(path => path.isAddedToLibrary)
})

// 优势亮点展开状态
const prosExpanded = ref({})

// 相似专利数据
const similarPatentsData = ref(props.patents || [])
// 解决方案库弹窗可见性
const solutionDialogVisible = ref(false)

// 定义组件发射的事件
const emit = defineEmits(['generate-solution', 'view-tech-path-detail', 'view-similar-patents', 'triplet-updated'])

// 组件挂载时，如果props.techPaths有数据，直接使用
onMounted(() => {
  if (props.techPaths && props.techPaths.length > 0) {
    console.log('使用外部传递的技术路径数据')
    // 初始化时，只有在localTechPaths为空的情况下才设置isAddedToLibrary为false
    // 避免覆盖已有的状态
    if (localTechPaths.value.length === 0) {
      localTechPaths.value = sortTechPaths(props.techPaths.map(path => ({
        ...path,
        isAddedToLibrary: false
      })))
    } else {
      // 如果localTechPaths已有数据，保持原有状态
      console.log('localTechPaths已有数据，保持原有状态')
    }
  }
})

// 加入库操作
const addToLibrary = (path, index) => {
  // 将技术路径标记为已加入库
  localTechPaths.value[index].isAddedToLibrary = true
  console.log(`技术路径 "${path.title}" 已加入库`)
}

// 从库中删除操作
const removeFromLibrary = (index) => {
  // 将技术路径标记为未加入库
  localTechPaths.value[index].isAddedToLibrary = false
  console.log(`技术路径已从库中删除`)
}

// 切换优势亮点展开状态
const togglePros = (index) => {
  prosExpanded.value[index] = !prosExpanded.value[index]
}

// 打开解决方案库弹窗
const openSolutionDialog = () => {
  console.log('openSolutionDialog called')
  // 检查是否有技术路径被选中加入库
  const selectedPaths = localTechPaths.value.filter(path => path.isAddedToLibrary)
  if (selectedPaths.length === 0) {
    // 显示提示信息
    ElMessage.warning('请先选择至少一个技术路径加入库')
    return
  }
  solutionDialogVisible.value = true
}

// 关闭解决方案库弹窗
const closeSolutionDialog = () => {
  solutionDialogVisible.value = false
}

// 生成解决方案
const generateSolution = () => {
  console.log('生成解决方案')
  // 如果没有从弹窗获取数据，使用默认数据
  const defaultSolutionData = {
    selectedTechPaths: selectedTechPaths,
    selectedPatentsData: similarPatentsData
  }
  emit('generate-solution', defaultSolutionData)
}

// 查看技术路径详情
const viewDetail = (techPath) => {
  console.log('查看技术路径详情:', techPath)
  emit('view-tech-path-detail', techPath)
}

// 查看相似专利
const viewSimilarPatents = (techPath) => {
  console.log('查看相似专利:', techPath)
  // 传递技术路径的description和id，用于查询相似专利 + 批量落库来源标记
  emit('view-similar-patents', {
    techPathDescription: techPath.description,
    patentSearchParams: techPath.patentSearchParams,
    pathId: techPath.id
  })
}
</script>

<style scoped lang="scss">
.analysis-result-section {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.result-info {
  display: flex;
  align-items: center;
  gap: 16px;
  
  span {
    font-size: 14px;
    color: #666;
  }
  
  .view-library-btn {
    padding: 14px 20px;
    font-size: 14px;
    color: #fff;
    background-color: #333;
    border-color: #333;
    &:hover {
      color: #fff;
    }
  }
}

/* 推荐方案 */
.recommended-solution,
.tech-path-item.recommended {
  background-color: #f0f9ff;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #90caf9;
}

.solution-header {
  margin-bottom: 12px;
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 8px 0 0 0;
  }
}

.solution-description {
  margin-bottom: 16px;
  
  p {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
}

.solution-actions {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  
  :deep(.el-button) {
    color: #666;
    padding: 0;
    height: auto;
    
    :deep(.el-icon) {
      margin-right: 4px;
    }
  }
}

/* 技术路径列表 */
.tech-path-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tech-path-item {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }
}

.path-header {
  margin-bottom: 12px;
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.path-description {
  margin-bottom: 16px;
  
  p {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
}

.path-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  
  :deep(.el-tag) {
    background-color: #f0f9ff;
    color: #1890ff;
    border-color: #90caf9;
  }
}

.path-pros {
  margin-bottom: 16px;
  
  .pros-header {
    display: flex;
    align-items: center;
    cursor: pointer;
    
    :deep(.el-icon) {
      color: #666;
      font-size: 16px;
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    h5 {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin: 0;
      display: flex;
      align-items: center;
    }
  }
  
  .pros-content {
    margin-top: 8px;
    
    .pros-section,
    .cons-section {
      margin-bottom: 12px;
      
      h6 {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin: 0 0 8px 0;
      }
      
      ul {
        margin: 0;
        padding-left: 20px;
        
        li {
          font-size: 14px;
          line-height: 1.5;
        }
      }
      
      &.pros-section {
        li {
          color: #52c41a;
        }
      }
      
      &.cons-section {
        li {
          color: #f56c6c;
        }
      }
    }
    
    p {
      font-size: 14px;
      color: #999;
      margin: 0;
    }
  }
}

.path-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  
  :deep(.el-button) {
    color: #666;
    padding: 0;
    height: auto;
    font-size: 14px;
    
    :deep(.el-icon) {
      margin-right: 4px;
    }
    
    &.el-button--primary {
      padding: 6px 12px;
      background-color: #1890ff;
      border-color: #1890ff;
      color: #fff;
      
      &:hover {
        background-color: #40a9ff;
        border-color: #40a9ff;
        color: #fff;
      }
    }
    
    &.remove-library-btn {
      color: #f56c6c;
      
      &:hover {
        color: #ff7875;
      }
    }
  }
}

.path-buttons {
  display: flex;
  gap: 8px;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 40px;

  .loading-icon {
    font-size: 40px;
    color: #1890ff;
    animation: spin 0.8s linear infinite;
  }

  .loading-hint {
    font-size: 14px;
    color: #909399;
    margin: 0;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>